<template>
    <AppModal :name="name"
              :beforeClose="beforeClose"
              :beforeOpen="beforeOpen"
              width="1000"
              height="98%">

        <template slot="header">
            Quick actions
        </template>

        <div slot="content" class="h-100 overflow-auto">

            <div class="w-100 text-left mt-3 m-auto" ref="tableContainer">
                <ag-grid-vue
                    @grid-ready="onGridReady"
                    style="height: 250px;"
                    class="ag-theme-balham config-table"
                    :columnDefs="agGrid.columnDefs"
                    :defaultColDef="agGrid.defaultColDef"
                    :rowData="rowsData"
                    @selectionChanged="onSelectionChanged"
                    rowSelection="multiple">
                </ag-grid-vue>
                <div class="row mx-0 mt-2">
                    <div class="col-6 p-0 text-left">
                        <button @click.prevent="onAddLine"
                                class="btn btn-sm border rounded clickable px-3 mr-2">
                            Add new action
                        </button>
                    </div>
                    <div class="col-6 text-right pr-0">
                        <div v-if="selectedRows.length !== 0" class="d-inline-block border rounded px-2"
                             style="height: 31px">
                            ({{ selectedRows.length }}) Selected {{ 'row' | pluralize(selectedRows.length) }}:
                            <button v-tooltip="'Duplicate selected lines'"
                                    @click="onDuplicateLines"
                                    class="btn btn-sm text-info ml-1">
                                <i class="fal fa-copy fa-lg"/>
                            </button>
                            <button @click="onRemoveLines"
                                    v-tooltip="'Delete selected lines'"
                                    class="btn btn-sm text-danger">
                                <i class="fal fa-trash-alt fa-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template slot="actions">
            <div class="text-right">
                <button @click="saveActions" class="btn btn-primary mt-3">OK</button>
            </div>
        </template>

    </AppModal>
</template>

<script>
import {getFormExtensionByName} from '@/types/document-viewer/extension'
import {mapGetters} from 'vuex'
import AgGridCellRendererContainer from './ag-grid/renderers/AgGridCellRendererContainer'
import axios from 'axios'
import AgGridCellEditorMultiselect
    from './../../../../documents/single/view/lines/ag-grid/editors/AgGridCellEditorMultiselect'
import {FieldEditorOptionsSources} from '@/types/document-viewer/field/modules/editor/select'
import {FieldEditorTypes, getFormFieldFormattedLabel} from '@/types/document-viewer/field/modules/editor'
import {quickActionAvailableIfOptions} from '@/views/documents/subtypes/settings/quick-actions/index'

export default {
    name: 'FormQuickActions',
    props: {
        name: {
            type: String,
            required: true
        },
        actions: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            agGrid: {
                columnDefs: [],
                defaultColDef: null,
                gridApi: null,
                columnApi: null
            },
            rowsData: [],
            selectedRows: []
        }
    },
    computed: {
        ...mapGetters('doc_viewer_settings', [
            'checkIsAvailableExtension'
        ]),
        ...mapGetters('doc_viewer', [
            'dtStatuses'
        ])
    },
    methods: {

        async onAddLine() {
            this.addLine()
            await this.$nextTick()
            this.agGrid.gridApi.ensureIndexVisible(this.rowsData.length - 1, 'bottom')
        },

        onRemoveLines() {
            for (const {lineIdx} of this.selectedRows) {
                this.removeLine(lineIdx)
            }
            this.selectedRows = []
        },

        async onDuplicateLines() {
            for (const {name, hotkey, workflow, status} of this.selectedRows) {
                this.addLine({
                    name,
                    hotkey,
                    workflow,
                    status
                })
            }
        },

        addLine(data) {
            if (!data) {
                data = {
                    name: null,
                    hotkey: null,
                    workflow: null,
                    status: null
                }
            }
            data.lineIdx = this.rowsData.length + 1
            this.rowsData.push(data)
        },

        removeLine(idx) {
            const position = this.rowsData.findIndex(row => row.lineIdx === idx)
            if (position === -1) {
                return
            }
            this.rowsData.splice(position, 1)
        },

        onSelectionChanged() {
            this.selectedRows = this.agGrid.gridApi.getSelectedRows()
        },

        onRowPropertyChange({lineIdx, property, value}) {
            if (!this.rowsData[lineIdx - 1].hasOwnProperty(property)) return

            this.rowsData[lineIdx - 1][property] = value
        },

        async fetchAutomationList() {
            this.isFetching = true

            try {
                const res = await axios.get(`automation/workflows`)

                if (res.data.data && res.data.data.length !== 0) {
                    return res.data.data.sort((a, b) => a.order - b.order)
                } else {
                    return []
                }
            } catch (error) {
                this.$snackbar.show({error})
            } finally {
                this.isFetching = false
            }
        },

        saveActions() {
            this.$emit('update', this.rowsData)
        },

        beforeOpen({params}) {
            this.rowsData = _.cloneDeep(this.actions)
        },

        beforeClose() {
        },

        close() {
            this.$modal.hide(this.name)
        },

        onChange(v) {
        },

        toggleExtension(extension) {
            const extensions = _.cloneDeep(this.extensions)
            const idx = extensions.findIndex(e => e.name === extension.name)
            if (idx === -1) {
                const data = {name: extension.name}
                if (extension.params) {
                    data.params = extension.params
                }
                extensions.push(data)
            } else {
                extensions.splice(idx, 1)
            }
            this.$emit('update', extensions)
        },

        onExtensionParamUpdate({extension, path, value}) {
            const extensions = _.cloneDeep(this.extensions)
            const idx = extensions.findIndex(e => e.name === extension.name)
            if (idx !== -1) {
                if (!extensions[idx].params) {
                    const extensionModel = getFormExtensionByName(extensions[idx].name)
                    if (extensionModel && _.isObject(extensionModel.params)) {
                        extensions[idx].params = _.cloneDeep(extensionModel.params)
                    } else {
                        extensions[idx].params = {}
                    }
                }
                _.set(extensions[idx].params, path, value)
            }
            this.$emit('update', extensions)
        },

        async onGridReady(params) {
            this.agGrid.gridApi = params.api
            this.agGrid.columnApi = params.columnApi

            this.agGrid.gridApi.showLoadingOverlay()

            this.agGrid.defaultColDef = {
                // width: 180,
                sortable: true,
                filter: true,
                editable: false,
                resizable: true,
                cellRendererFramework: AgGridCellRendererContainer
            }

            await this.setupColumns()

            this.agGrid.gridApi.sizeColumnsToFit()
            this.agGrid.gridApi.setFillHandleDirection('y')
            this.agGrid.gridApi.hideOverlay()
        },

        async setupColumns() {
            this.agGrid.columnDefs = [
                {
                    headerName: '#',
                    field: 'lineIdx',
                    width: 50
                }, {
                    field: 'name',
                    editable: true
                },
                {
                    field: 'hotkey',
                    height: 100,
                    autoHeight: true
                },
                {
                    editable: true,
                    field: 'workflow',
                    cellEditorFramework: AgGridCellEditorMultiselect,
                    cellRendererFramework: null,
                    cellEditorParams: {
                        settings: {
                            editor: {
                                type: FieldEditorTypes.DD_SINGLE,
                                select: {
                                    source: FieldEditorOptionsSources.STATIC,
                                    options: await this.fetchAutomationList() || [],
                                    trackBy: 'uuid',
                                    label: 'name'
                                }
                            }
                        }
                    },
                    valueGetter(params) {
                        return getFormFieldFormattedLabel({
                            editor: {
                                type: FieldEditorTypes.DD_SINGLE,
                                select: {
                                    label: 'name'
                                }
                            },
                            value: params.data.workflow
                        })
                    }
                },
                {
                    field: 'status',
                    editable: true,
                    cellEditorFramework: AgGridCellEditorMultiselect,
                    cellEditorParams: {
                        settings: {
                            editor: {
                                type: FieldEditorTypes.DD_SINGLE,
                                select: {
                                    source: FieldEditorOptionsSources.STATIC,
                                    options: this.dtStatuses,
                                    trackBy: 'uuid',
                                    label: 'name'
                                }
                            }
                        }
                    }
                }, {
                    field: 'availableIf',
                    headerName: 'Available',
                    editable: true,
                    cellEditorFramework: AgGridCellEditorMultiselect,
                    cellEditorParams: {
                        settings: {
                            editor: {
                                type: FieldEditorTypes.DD_SINGLE,
                                select: {
                                    source: FieldEditorOptionsSources.STATIC,
                                    options: quickActionAvailableIfOptions,
                                    trackBy: 'key',
                                    label: 'name'
                                }
                            }
                        }
                    },
                    valueGetter(params) {
                        const option = quickActionAvailableIfOptions.find(o => o.key === params.data.availableIf)
                        return _.get(option, 'name', '')
                    },
                    valueSetter(params) {
                        const option = quickActionAvailableIfOptions.find(o => o.key === params.newValue.key)
                        if (option) {
                            params.data.availableIf = option.key
                            return true
                        }
                        return false
                    }
                }
            ]
        }
    }
}
</script>

<style scoped lang="scss">
.extension-level-header {
    font-size: 18px;
    font-weight: 500;

    &:hover {
        cursor: pointer;
    }
}
</style>
